// Etiqueta exacta de las gráficas de la Sala (Pau 15-10-2026: «pasar el ratón por encima de las barras y ver el número
// exacto… lo mismo en el pulso, en las líneas y las métricas»). Una sola pieza para TODAS las gráficas, así se leen igual:
//   · usePuntero: qué posición (barra, hora, día) está bajo el ratón, el dedo o el teclado. El ratón se pega a la
//     posición más cercana (no hay que acertar en una línea de 2 px); en el móvil, tocar o arrastrar en horizontal
//     (el desplazamiento vertical de la página sigue funcionando); con el teclado, Tab y flechas ← →; Esc cierra.
//   · Etiqueta: la etiqueta flotante. Cabecera (la hora o el día), una fila por serie con su marca de color y el número
//     exacto en grande, y un pie opcional (estimado, a medias, clic para abrir…). Va al lado de la marca, nunca encima,
//     y cambia de lado al pasar la mitad de la gráfica para no salirse.
// Coordenadas: `x` en unidades del viewBox de la gráfica y `ancho` el ancho del viewBox; la posición se da en % del
// contenedor, así vale aunque el SVG se escale (tests, gráficas de ancho fijo).
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactElement, type ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';

export interface FilaEtiqueta {
  etiqueta: string;
  valor: string;
  /** Marca de la serie: clase de fondo (bg-…; border-… si es «discontinua»). Sin marca, la fila es un dato suelto. */
  marca?: string;
  /** Forma de la marca: raya para líneas, cuadrado para barras/áreas. */
  forma?: 'linea' | 'discontinua' | 'barra';
  /** Fila secundaria (referencia, media): letra más tenue. */
  tenue?: boolean;
  /** Empieza un bloque nuevo (el reparto tras los totales): una raya fina encima. */
  separada?: boolean;
}

/** Posición bajo el puntero. `indiceDe` traduce una x del viewBox a la posición más cercana (o null si no hay). */
export function usePuntero({
  n,
  ancho,
  indiceDe,
  valido = () => true,
  externo,
}: {
  n: number;
  ancho: number;
  indiceDe: (x: number) => number | null;
  /** Posiciones que no se pueden marcar con el teclado (huecos sin dato). */
  valido?: (i: number) => boolean;
  /** Estado compartido con otras gráficas (HoverDia); por defecto, propio. */
  externo?: [number | null, (i: number | null) => void];
}) {
  const propio = useState<number | null>(null);
  const [i, fijar] = externo ?? propio;
  // Esta gráfica es la que tiene el puntero (con estado compartido, solo ella enseña la etiqueta completa).
  const [activa, setActiva] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const tactil = useRef(false);
  const antes = useRef<number | null>(null);

  const desde = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      const r = e.currentTarget.getBoundingClientRect();
      if (!r.width) return;
      fijar(indiceDe(((e.clientX - r.left) * ancho) / r.width));
      setActiva(true);
    },
    [ancho, fijar, indiceDe],
  );

  // En el móvil la etiqueta se queda al levantar el dedo; se cierra al tocar fuera.
  useEffect(() => {
    if (!activa) return;
    const fuera = (e: globalThis.PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setActiva(false);
        fijar(null);
      }
    };
    document.addEventListener('pointerdown', fuera);
    return () => document.removeEventListener('pointerdown', fuera);
  }, [activa, fijar]);

  const mover = (paso: number) => {
    let j = i ?? (paso > 0 ? -1 : n);
    for (let k = 0; k < n; k++) {
      j += paso;
      if (j < 0 || j >= n) return;
      if (valido(j)) break;
    }
    if (j >= 0 && j < n && valido(j)) {
      fijar(j);
      setActiva(true);
    }
  };

  const props = {
    ref,
    tabIndex: 0,
    style: { touchAction: 'pan-y' } as const,
    onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
      tactil.current = e.pointerType !== 'mouse';
      antes.current = i;
      desde(e);
    },
    onPointerMove: desde,
    onPointerLeave: () => {
      if (tactil.current) return;
      setActiva(false);
      fijar(null);
    },
    onBlur: () => {
      if (tactil.current) return;
      setActiva(false);
      fijar(null);
    },
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        mover(e.key === 'ArrowRight' ? 1 : -1);
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        fijar(null);
        mover(e.key === 'End' ? -1 : 1);
      } else if (e.key === 'Escape') {
        e.preventDefault(); // cierra la etiqueta de la gráfica, no la sala
        setActiva(false);
        fijar(null);
      }
    },
  };
  /** Un clic abre la posición marcada; en el móvil, el primer toque solo enseña la etiqueta y el segundo la abre. */
  const confirmado = () => !tactil.current || antes.current === i;
  return { i, activa: activa && i !== null, props, confirmado };
}

const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;
/** «14:00 – 15:00 UTC» (la última, «23:00 – 24:00 UTC»: el día de la Sala es UTC). */
export const franjaHora = (h: number) => t('salaControl.grafica.franjaHora', { desde: hh(h), hasta: hh(h + 1) });
/** «Hasta las 14:00 UTC» (acumulados). */
export const hastaHora = (h: number) => t('salaControl.grafica.hastaHora', { hora: hh(h) });

/** Anillo de foco de una gráfica (se recorre con Tab y ← →). */
export const FOCO = 'rounded-md outline-none focus-visible:ring-2 focus-visible:ring-oficina-acento/60';

const MARCA = {
  linea: 'h-[3px] w-3 rounded-full',
  discontinua: 'h-0 w-3 border-t-2 border-dashed bg-transparent',
  barra: 'h-2.5 w-2.5 rounded',
} as const;

/** Separación entre la marca y la etiqueta (px). */
const HUECO = 14;

/** El primer contenedor que recorta lo que se sale (la ventana con scroll de la Sala). */
function recortador(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const e = getComputedStyle(p);
    if (e.overflowX !== 'visible' || e.overflowY !== 'visible') return p;
  }
  return null;
}

/** Etiqueta flotante con el número exacto. Se pinta dentro de un contenedor `relative` que ocupa la gráfica. */
export function Etiqueta({
  x,
  ancho,
  titulo,
  filas,
  pie,
  arriba = 0,
}: {
  x: number;
  ancho: number;
  titulo: ReactNode;
  filas: FilaEtiqueta[];
  pie?: ReactNode;
  /** Distancia desde arriba del contenedor (px). */
  arriba?: number;
}) {
  const f = ancho > 0 ? Math.min(Math.max(x / ancho, 0), 1) : 0.5;
  const ref = useRef<HTMLDivElement>(null);
  // Posición final (px desde la izquierda del contenedor). Al lado de la marca, del lado con más sitio; si ahí no cabe
  // entera dentro de lo visible (gráficas pequeñas pegadas a un borde de la ventana), al otro lado; si tampoco, lo justo
  // para no cortarse.
  const [izq, setIzq] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const caja = el?.offsetParent as HTMLElement | null;
    if (!el || !caja) return;
    const c = caja.getBoundingClientRect();
    const w = el.offsetWidth;
    const marca = c.left + f * c.width;
    const vis = recortador(caja)?.getBoundingClientRect() ?? { left: 0, right: window.innerWidth };
    const min = Math.max(vis.left, 0) + 4;
    const max = Math.min(vis.right, window.innerWidth) - 4;
    const aDer = marca + HUECO;
    const aIzq = marca - HUECO - w;
    const cabe = (l: number) => l >= min && l + w <= max;
    const prefiere = f > 0.5 ? [aIzq, aDer] : [aDer, aIzq];
    const l = prefiere.find(cabe) ?? Math.min(Math.max(prefiere[0]!, min), max - w);
    setIzq(l - c.left);
  }, [f, titulo, filas]);
  return (
    <div
      ref={ref}
      role="tooltip"
      aria-live="polite"
      data-testid="etiqueta-grafica"
      className="pointer-events-none absolute z-30 min-w-[9rem] max-w-[22rem] rounded-o-m border border-oficina-linea bg-oficina-hover px-3 py-2.5 text-o-nota leading-4 shadow-o-hoja"
      style={{
        top: arriba,
        ...(izq !== null ? { left: izq } : f > 0.5 ? { right: `calc(${(1 - f) * 100}% + ${HUECO}px)` } : { left: `calc(${f * 100}% + ${HUECO}px)` }),
      }}
    >
      <Contenido titulo={titulo} filas={filas} pie={pie} />
    </div>
  );
}

/** Lo de dentro de una etiqueta (gráficas y cifras exactas): cabecera, filas con su marca y pie. */
function Contenido({ titulo, filas, pie }: { titulo?: ReactNode; filas: FilaEtiqueta[]; pie?: ReactNode }) {
  return (
    <>
    {titulo ? <p className="mb-1.5 whitespace-nowrap font-medium text-oficina-texto2">{titulo}</p> : null}
    <ul className="flex flex-col gap-1">
      {filas.map((r) => (
        <li key={r.etiqueta || 'valor'} className={cn('flex items-baseline justify-between gap-4 whitespace-nowrap', r.separada && 'mt-1 border-t border-oficina-linea pt-2')}>
          {r.etiqueta ? (
            <span className={cn('flex min-w-0 items-center gap-1.5', r.tenue ? 'text-oficina-texto3' : 'text-oficina-texto2')}>
              {r.marca ? <i aria-hidden className={cn('inline-block shrink-0 self-center', MARCA[r.forma ?? 'linea'], r.marca)} /> : null}
              <span className="truncate">{r.etiqueta}</span>
            </span>
          ) : (
            // Sin nombre (la gráfica ya lo dice en su título): la cifra sola, grande, con su marca.
            <span className="flex items-center gap-2 text-o-cuerpo font-semibold text-oficina-texto">
              {r.marca ? <i aria-hidden className={cn('inline-block shrink-0 self-center', MARCA[r.forma ?? 'linea'], r.marca)} /> : null}
              <span className="cifras tabular-nums">{r.valor}</span>
            </span>
          )}
          {r.etiqueta ? (
            <span className={cn('shrink-0 cifras tabular-nums', r.tenue ? 'text-oficina-texto2' : 'text-o-texto font-semibold text-oficina-texto')}>{r.valor}</span>
          ) : null}
        </li>
      ))}
    </ul>
    {pie ? <p className="mt-1.5 border-t border-oficina-linea pt-1.5 text-oficina-texto3">{pie}</p> : null}
    </>
  );
}

/** Cifra redondeada (titulares y tarjetas) que enseña su valor exacto al pasar el ratón, al enfocarla o al tocarla.
 *  `children` es el propio elemento de la cifra: se le añade el foco, sin envolverlo. */
export function Exacto({ children, filas, titulo }: { children: ReactElement; filas: FilaEtiqueta[]; titulo?: ReactNode }) {
  return (
    <TooltipPrimitive.Root delayDuration={120}>
      <TooltipPrimitive.Trigger asChild tabIndex={0} className={cn('cursor-default', FOCO)}>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={8}
          data-testid="cifra-exacta"
          className="z-[80] min-w-[9rem] max-w-[22rem] rounded-o-m border border-oficina-linea bg-oficina-hover px-3 py-2.5 text-o-nota leading-4 shadow-o-hoja animate-in fade-in-0"
        >
          <Contenido titulo={titulo} filas={filas} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
